import React, { useState, useEffect } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import { showNotification } from "aldoo-ra/Notification"
import { showPrimaryPackConfirmDialog } from "./PrimaryPackConfirmDialog"
import { CreatePack, UpdatePack } from "./api"

export default function AddPackDialog({ isOpen, pack, onClose, onPackSaved }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    state: "active",
    primary: false,
    external: false,
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // Reset form when dialog opens/closes or pack changes
  useEffect(() => {
    if (isOpen) {
      if (pack) {
        // Editing existing pack
        setFormData({
          name: pack.name || "",
          description: pack.description || "",
          state: pack.state || "active",
          primary: pack.primary || false,
          external: pack.external || false,
        })
      } else {
        // Creating new pack
        setFormData({
          name: "",
          description: "",
          state: "active",
          primary: false,
          external: false,
        })
      }
      setErrors({})
    }
  }, [isOpen, pack])

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value }
      
      // Business logic: external packs cannot be primary
      if (field === "external" && value === true) {
        newData.primary = false
      }
      
      return newData
    })
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }))
    }
  }

  // Handle primary toggle with confirmation
  const handlePrimaryToggle = (isPrimary) => {
    // If setting to primary, show confirmation dialog
    if (isPrimary) {
      showPrimaryPackConfirmDialog(() => {
        handleInputChange("primary", isPrimary)
      })
    } else {
      // If removing primary, proceed directly
      handleInputChange("primary", isPrimary)
    }
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = "Pack name is required"
    } else if (formData.name.trim().length < 3) {
      newErrors.name = "Pack name must be at least 3 characters"
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = "Description must be less than 500 characters"
    }

    // Validate external and primary mutual exclusion
    if (formData.external && formData.primary) {
      newErrors.primary = "External packs cannot be set as primary"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) return

    setLoading(true)
    try {
      const packData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        state: formData.state,
        primary: formData.primary,
        external: formData.external,
      }

      let response
      if (pack && pack._id) {
        // Update existing pack
        response = await UpdatePack({
          packId: pack._id,
          ...packData,
        })
      } else {
        // Create new pack
        response = await CreatePack(packData)
      }

      if (response.result) {
        showNotification({
          message: `Pack ${pack ? "updated" : "created"} successfully`,
          className: "bg-admin_primary text-admin_text_inv",
        })
        onPackSaved()
      } else {
        showNotification({
          message:
            response.error || `Failed to ${pack ? "update" : "create"} pack`,
          className: "bg-admin_error text-white",
        })
      }
    } catch (error) {
      // Extract error message from different possible sources
      let errorMessage = `An error occurred while ${pack ? "updating" : "creating"} pack`
      
      // Check if it's an axios error with response data
      if (error.response && error.response.data) {
        const responseData = error.response.data
        
        // Handle specific backend validation errors
        if (responseData.error === "external_cannot_be_primary") {
          errorMessage = "External packs cannot be set as primary. Please uncheck the primary option."
        } else if (responseData.error === "invalid_external") {
          errorMessage = "Invalid external field value. Please check your input."
        } else {
          errorMessage = responseData.message || responseData.error || error.message
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      showNotification({
        message: errorMessage,
        className: "bg-admin_error text-white",
      })
    } finally {
      setLoading(false)
    }
  }

  // Generate pack name suggestions
  const generatePackNameSuggestions = () => {
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().toLocaleDateString("en-US", {
      month: "long",
    })

    return [
      `${currentMonth} ${currentYear} Pack`,
      `Q${Math.ceil((new Date().getMonth() + 1) / 3)} ${currentYear} Licenses`,
      "Enterprise Client Pack",
      "Bulk License Distribution",
      "Partner Program Licenses",
    ]
  }

  if (!isOpen) return null

  return (
    <Modal
      variant="admin"
      isOpen={isOpen}
      onClose={onClose}
      title={pack ? "Edit License Pack" : "Create License Pack"}
      className="max-w-xl"
    >
      <div className="space-y-6">
        {/* Pack Name */}
        <div>
          <Typography
            variant="admin_label"
            className="text-admin_text"
          >
            Pack Name *
          </Typography>

          <Input
            variant="admin"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="e.g., Q1 2025 Enterprise Licenses"
            error={errors.name}
            className="w-full"
            showClearButton
            autoFocus
          />

          {!pack && formData.name.length === 0 && (
            <div className="mt-2">
              <Typography
                variant="admin_txt2_dim"
              >
                Suggestions:
              </Typography>

              <div className="flex flex-wrap gap-2 mt-2">
                {generatePackNameSuggestions().map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    className="text-xs bg-admin_neutral_1/50 hover:bg-admin_neutral_1 text-admin_text_secondary rounded-full transition-colors px-3 py-1"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, name: suggestion }))
                    }
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <Typography
            variant="admin_label"
          >
            Description (Optional)
          </Typography>

          <Input
            variant="admin"
            multiline={true}
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            placeholder="Describe the purpose or context of this license pack..."
            error={errors.description}
            className="w-full"
            rows={4}
          />

          <div className="flex justify-between mt-1">
            <Typography
              variant="admin_txt2_dim"
            >
              Helps identify the pack's purpose and target audience
            </Typography>

            <Typography
              variant="admin_txt2_dim"
            >
              {formData.description.length}/500
            </Typography>
          </div>
        </div>

        {/* Pack State - Only show when creating new pack */}
        {!pack && (
          <div>
            <Typography
              variant="admin_label"
            >
              Initial State
            </Typography>

            <DropDownSelect
              variant="admin"
              options={[
                {
                  value: "active",
                  label: "Active",
                  description:
                    "Pack is ready for license generation and distribution",
                },
                {
                  value: "paused",
                  label: "Paused",
                  description: "Pack exists but license generation is disabled",
                },
              ]}
              value={[
                {
                  value: formData.state,
                  label: formData.state === "active" ? "Active" : "Paused",
                },
              ]}
              onChange={(values) =>
                handleInputChange("state", values?.[0]?.value || "active")
              }
              className="w-full"
            />
            <Typography
              variant="admin_txt2_dim"
              className="mt-1"
            >
              {formData.state === "active"
                ? "Licenses can be generated immediately after creation"
                : "Pack will be created but license generation will be disabled"}
            </Typography>
          </div>
        )}

        {/* Pack Type - External */}
        <div>
          <Typography
            variant="admin_label"
          >
            Pack Type
          </Typography>

          <div className="flex items-start justify-between">
            <div>
              <Typography variant="admin_body" className="my-0">
                External Pack
              </Typography>

              <Typography
                variant="admin_txt2_dim"
              >
                For partner products or external systems. Activation tracking is handled externally.
              </Typography>
            </div>

            <ToggleSwitch
              variant="admin"
              isOn={formData.external}
              onChange={(isExternal) => handleInputChange("external", isExternal)}
            />
          </div>
        </div>

        {/* Primary Pack - Only show if not external */}
        {!formData.external && (
          <div>
            <Typography
              variant="admin_label"
            >
              Primary Pack
            </Typography>

            <div className="flex items-start justify-between">
              <div>
                <Typography variant="admin_body" className="my-0">
                  Set as Primary
                </Typography>

                <Typography
                  variant="admin_txt2_dim"
                >
                  System-generated licenses will be placed here by default
                </Typography>
              </div>

              <ToggleSwitch
                variant="admin"
                isOn={formData.primary}
                onChange={handlePrimaryToggle}
              />
            </div>
          </div>
        )}

        {/* Warning message when external is selected */}
        {formData.external && (
          <div className="bg-admin_canvas border border-admin_warning/20 rounded-lg p-4">
            <Typography variant="admin_txt1" className="text-admin_warning">
              External packs cannot be set as primary. They are designed for partner products and external activation systems.
            </Typography>
          </div>
        )}
      </div>

      {/* Dialog Actions */}
      <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-admin_bordered">
        <Button 
          variant="admin_secondary" 
          onClick={onClose} disabled={loading}
        >
          Cancel
        </Button>

        <Button 
          variant="admin_primary" 
          onClick={handleSave} loading={loading}
        >
          {pack ? "Update Pack" : "Create Pack"}
        </Button>
      </div>
    </Modal>
  )
}
